import { fetchAuthSessionWithLoginMismatchRetry } from "../../utils/amplifyAuthRecovery";
import { t } from "@/localization";
import { LIBRARY_API_URL } from "./config";

// evt.live routes behind Download everything. All of them act on the signed-in
// account only (evt.live checks the access token and that every event is the
// account's own):
//   GET  /api/library?cursor=…        the account's recordings, a page at a time
//   POST /api/library/recording       { slug } → the recording's MP4: ready
//                                     (with a link), preparing, or archived
//   POST /api/library/link            { file, slug? } → a short-lived signed
//                                     link to one data file (CSV or PDF)

const REQUEST_TIMEOUT_MS = 30000;

// Shown on the screen (Download everything), in the app's language.
const STATUS_MESSAGES = {
  401: t("Please sign in again."),
  403: t("This recording belongs to another account."),
  404: t("We couldn't find this recording. It may have been deleted."),
  429: t("Too many requests. Please wait a moment and try again."),
};
const UNAVAILABLE_MESSAGE = t(
  "Downloads are temporarily unavailable. Please try again in a few minutes."
);
const OFFLINE_MESSAGE = t(
  "Could not reach EventLive. Check your internet connection and try again."
);

export class LibraryRequestError extends Error {
  constructor(message, status = 0) {
    super(message);
    this.name = "LibraryRequestError";
    this.status = status;
  }
}

// The API's own short, plain-text errors are shown as they are; HTML error
// pages and stack traces get a friendly message instead.
const apiErrorMessage = (data) => {
  const message =
    data && typeof data.error === "string" ? data.error.trim() : "";
  return message && message.length <= 200 && !/[<>{}]/.test(message)
    ? message
    : "";
};

const getAccessToken = async () => {
  try {
    const session = await fetchAuthSessionWithLoginMismatchRetry();
    return session?.tokens?.accessToken?.toString() || null;
  } catch (error) {
    return null;
  }
};

async function request(path, body) {
  const token = await getAccessToken();
  if (!token) throw new LibraryRequestError(STATUS_MESSAGES[401], 401);

  const controller =
    typeof AbortController !== "undefined" ? new AbortController() : null;
  const timeout = controller
    ? setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
    : null;

  let response;
  try {
    response = await fetch(`${LIBRARY_API_URL}${path}`, {
      method: body ? "POST" : "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body ? { "Content-Type": "application/json" } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
      ...(controller ? { signal: controller.signal } : {}),
    });
  } catch (error) {
    throw new LibraryRequestError(OFFLINE_MESSAGE, 0);
  } finally {
    if (timeout) clearTimeout(timeout);
  }

  let data = null;
  try {
    const text = await response.text();
    data = text ? JSON.parse(text) : null;
  } catch (error) {
    data = null;
  }

  if (!response.ok) {
    throw new LibraryRequestError(
      apiErrorMessage(data) ||
        STATUS_MESSAGES[response.status] ||
        UNAVAILABLE_MESSAGE,
      response.status
    );
  }
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    throw new LibraryRequestError(UNAVAILABLE_MESSAGE, response.status);
  }
  return data;
}

// One page of recordings: { recordings: [...], next_cursor }.
export const fetchLibraryPage = (cursor) =>
  request(
    `/api/library${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`
  );

// Every page, in order. onPage gets the list so far, so the screen can show
// the first recordings while the rest load.
export async function fetchLibrary({ onPage, maxPages = 100 } = {}) {
  const recordings = [];
  const seen = new Set();
  let cursor = null;
  for (let page = 0; page < maxPages; page++) {
    const data = await fetchLibraryPage(cursor);
    recordings.push(...(Array.isArray(data.recordings) ? data.recordings : []));
    onPage?.(recordings.slice());
    cursor = data.next_cursor || null;
    if (!cursor || seen.has(cursor)) break;
    seen.add(cursor);
  }
  return recordings;
}

// The recording's MP4. evt.live starts preparing it when it has to be built
// (or rebuilt because the replay changed) and answers right away:
//   { status: "ready", url, filename, size_bytes }
//   { status: "preparing", retry_after_s }
//   { status: "archived" }       kept in long-term storage (Request download)
//   { status: "unavailable", error }
// check: true only asks how it's going (polling), so it never queues the
// build again.
export const requestRecording = (slug, { check = false } = {}) =>
  request("/api/library/recording", check ? { slug, check: true } : { slug });

// A signed link to one data file, valid for a few minutes:
//   file: "events" | "guests" | "guestbook" | "viewers" | "sales" (CSV for
//   the whole account) or "guestbook_pdf" with the event's slug.
export const fetchFileLink = (file, slug) =>
  request("/api/library/link", slug ? { file, slug } : { file });
